// components/configuration/SettingsSection.jsx
import React from 'react';
import { ArrowPathIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

/**
 * Reusable wrapper for settings sections with consistent save/reset buttons
 * @param {string} title - Section title
 * @param {string} description - Optional section description
 * @param {ReactNode} children - Form content
 * @param {Function} onSave - Save callback
 * @param {Function} onReset - Reset callback
 * @param {string} saveStatus - Current save status: null | 'saving' | 'saved'
 * @param {boolean} showActions - Whether to show save/reset buttons
 * @param {string} successMessage - Custom success message
 */
const SettingsSection = ({ 
  title, 
  description,
  children, 
  onSave, 
  onReset, 
  saveStatus = null,
  showActions = true,
  successMessage = 'Settings saved successfully!'
}) => {
  return (
    <div className="space-y-6">
      {/* Header */}
      {(title || description) && (
        <div>
          {title && <h2 className="text-lg font-semibold text-gray-900 mb-2">{title}</h2>}
          {description && <p className="text-sm text-gray-600">{description}</p>}
        </div>
      )}

      {/* Content */}
      <div>{children}</div>

      {/* Save Status Message */}
      {saveStatus === 'saved' && (
        <div className="flex items-center text-green-600 bg-green-50 p-3 rounded-lg">
          <CheckCircleIcon className="h-5 w-5 mr-2" />
          <span className="text-sm font-medium">{successMessage}</span>
        </div>
      )}

      {/* Action Buttons */}
      {showActions && (
        <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            onClick={onReset}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors text-sm font-medium"
          >
            Reset to Defaults
          </button>
          <button
            onClick={onSave}
            disabled={saveStatus === 'saving'}
            className={`px-4 py-2 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center space-x-2 ${
              saveStatus === 'saving'
                ? 'bg-blue-400 text-white cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {saveStatus === 'saving' ? (
              <>
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <span>Save Changes</span>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default SettingsSection;